import React, { useState, useEffect } from 'react';
import { authService } from '../services/auth.ts';
import NavigationMenu from './NavigationMenu.tsx';

interface AboutPageProps {
  currentTeam: { teamId: string; teamName: string } | null;
  onBack: () => void;
  onNavigateToPastTopics: () => void;
  onNavigateToMyTopicStats: () => void;
  onNavigateToDeletedTopics: () => void;
  onSubmitNewTopic: () => void;
}

const AboutPage: React.FC<AboutPageProps> = ({ 
  currentTeam, 
  onBack, 
  onNavigateToPastTopics, 
  onNavigateToMyTopicStats,
  onNavigateToDeletedTopics, 
  onSubmitNewTopic 
}) => {
  const [showUserDropdown, setShowUserDropdown] = useState(false);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showUserDropdown) {
        setShowUserDropdown(false);
      }
    };

    if (showUserDropdown) {
      document.addEventListener('click', handleClickOutside);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [showUserDropdown]);

  const handleNavigation = (page: 'main' | 'past-topics' | 'my-topic-stats' | 'deleted-topics' | 'submit-topic') => {
    switch (page) {
      case 'main':
        onBack();
        break;
      case 'past-topics':
        onNavigateToPastTopics();
        break;
      case 'my-topic-stats':
        onNavigateToMyTopicStats();
        break;
      case 'deleted-topics':
        onNavigateToDeletedTopics();
        break;
      case 'submit-topic':
        onSubmitNewTopic();
        break;
    }
  };

  return (
    <div className="app">
      <header className="app-header">
        <div>
          <h1 style={{ margin: 0 }}>About ShareBrief</h1>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
          {(() => {
            const userInfo = authService.getUserInfo();
            return userInfo ? (
              <div style={{ position: 'relative' }}>
                <div 
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowUserDropdown(!showUserDropdown);
                  }}
                  style={{ 
                    fontSize: '16px', 
                    marginBottom: '8px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}
                >
                  Welcome <span style={{ color: '#2563eb', fontWeight: '600' }}>{userInfo.name}</span>
                  <span style={{ fontSize: '12px', color: '#666' }}>▼</span>
                </div>
                
                {showUserDropdown && (
                  <div 
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      position: 'absolute',
                      top: '100%',
                      right: '0',
                      backgroundColor: 'white',
                      border: '1px solid #ccc',
                      borderRadius: '4px',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                      zIndex: 9999,
                      minWidth: '120px'
                    }}
                  >
                    <button
                      onClick={() => {
                        authService.logout();
                      }}
                      style={{
                        display: 'block',
                        width: '100%',
                        padding: '8px 12px',
                        border: 'none',
                        background: 'none',
                        textAlign: 'left',
                        cursor: 'pointer',
                        fontSize: '14px'
                      }}
                      onMouseEnter={(e) => (e.target as HTMLElement).style.backgroundColor = '#f5f5f5'}
                      onMouseLeave={(e) => (e.target as HTMLElement).style.backgroundColor = 'transparent'}
                    >
                      Logout
                    </button>
                  </div>
                )}
              </div>
            ) : null;
          })()}
        </div>
      </header>
      
      <NavigationMenu 
        currentPage="about" 
        currentTeamId={currentTeam?.teamId}
        onNavigate={handleNavigation}
      />

      <div className="main-content">
        <div className="about-content" style={{ 
          maxWidth: '800px', 
          margin: '0 auto', 
          padding: '20px 0',
          lineHeight: '1.6'
        }}>
          
          <section style={{ marginBottom: '32px' }}>
            <h2 style={{ color: '#2563eb', marginBottom: '16px', textAlign: 'center' }}>What is ShareBrief?</h2>
            <p style={{ textAlign: 'center' }}>
              ShareBrief is a knowledge sharing platform designed to accelerate customer conversations through 
              organized team collaboration. Transform individual expertise into collective knowledge that helps 
              everyone respond to customer questions faster and more confidently.
            </p>
          </section>

          <section style={{ marginBottom: '32px' }}>
            <h2 style={{ color: '#2563eb', marginBottom: '16px', textAlign: 'center' }}>Purpose</h2>
            <p style={{ textAlign: 'center' }}>
              <strong>Inspire building, reduce research time, and accelerate customer success</strong> by making team knowledge 
              easily accessible and trackable.
            </p>
          </section>

          <section style={{ marginBottom: '32px' }}>
            <h2 style={{ color: '#2563eb', marginBottom: '16px', textAlign: 'center' }}>Key Features</h2>
            
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: '1fr 1fr', 
              gap: '24px',
              '@media (max-width: 768px)': { gridTemplateColumns: '1fr' }
            }}>
              <div>
                <h3 style={{ color: '#1e40af', marginBottom: '12px', marginTop: '24px' }}>📅 Topic Scheduling & Session Management</h3>
                <ul style={{ paddingLeft: '20px', marginBottom: '20px' }}>
                  <li>Organize knowledge sharing sessions with flexible scheduling</li>
                  <li>Submit topics for upcoming sessions or future planning</li>
                  <li>Track session attendance and participation</li>
                </ul>

                <h3 style={{ color: '#1e40af', marginBottom: '12px' }}>📊 Impact Tracking & Analytics</h3>
                <ul style={{ paddingLeft: '20px', marginBottom: '20px' }}>
                  <li>Measure confidence boost from each topic presentation</li>
                  <li>Track preparation time saved for customer conversations</li>
                  <li>Track customer conversations influenced by team expertise</li>
                  <li>View personal impact dashboard with detailed statistics</li>
                </ul>
              </div>

              <div>
                <h3 style={{ color: '#1e40af', marginBottom: '12px', marginTop: '24px' }}>🤝 Cross-Series Collaboration</h3>
                <ul style={{ paddingLeft: '20px', marginBottom: '20px' }}>
                  <li>Share expertise beyond immediate team boundaries</li>
                  <li>Teams create their own knowledge sharing series</li>
                  <li>Presenters can submit and present topics to different teams</li>
                </ul>

                <h3 style={{ color: '#1e40af', marginBottom: '12px' }}>💬 Feedback & Engagement</h3>
                <ul style={{ paddingLeft: '20px', marginBottom: '20px' }}>
                  <li>Collect and track structured feedback on topic impact</li>
                  <li>Share direct links with colleagues to submit their feedback</li>
                </ul>

                <h3 style={{ color: '#1e40af', marginBottom: '12px' }}>🎯 Meeting Preparation <em>(Coming Soon)</em></h3>
                <ul style={{ paddingLeft: '20px', marginBottom: '20px' }}>
                  <li>AI-powered meeting preparation notes generation</li>
                  <li>Leverage past topics for customer conversation preparation</li>
                </ul>
              </div>
            </div>
          </section>

          <section style={{ marginBottom: '32px' }}>
            <h2 style={{ color: '#2563eb', marginBottom: '16px', textAlign: 'center' }}>Benefits</h2>
            
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: '1fr 1fr', 
              gap: '24px',
              '@media (max-width: 768px)': { gridTemplateColumns: '1fr' }
            }}>
              <div>
                <h3 style={{ color: '#1e40af', marginBottom: '12px', marginTop: '24px' }}>For Individuals</h3>
                <ul style={{ paddingLeft: '20px', marginBottom: '20px' }}>
                  <li><strong>Faster Customer Responses</strong> - Access team knowledge instantly</li>
                  <li><strong>Confidence Building</strong> - Learn from colleagues' expertise</li>
                  <li><strong>Skill Development</strong> - Practice presentation and technical skills</li>
                  <li><strong>Recognition</strong> - Track and showcase your knowledge sharing impact</li>
                </ul>

                <h3 style={{ color: '#1e40af', marginBottom: '12px' }}>For Teams</h3>
                <ul style={{ paddingLeft: '20px', marginBottom: '20px' }}>
                  <li><strong>Knowledge Retention</strong> - Preserve expertise beyond individual team members</li>
                  <li><strong>Reduced Workload</strong> - Distribute specialist knowledge across the team</li>
                  <li><strong>Accelerated Onboarding</strong> - New team members access historical knowledge</li>
                  <li><strong>Improved Collaboration</strong> - Foster culture of knowledge sharing</li>
                </ul>
              </div>

              <div>
                <h3 style={{ color: '#1e40af', marginBottom: '12px', marginTop: '24px' }}>For Organizations</h3>
                <ul style={{ paddingLeft: '20px', marginBottom: '20px' }}>
                  <li><strong>Customer Success</strong> - Faster, more informed customer interactions</li>
                  <li><strong>Opportunity Acceleration</strong> - Reduce time from question to solution</li>
                  <li><strong>Expertise Scaling</strong> - Multiply individual knowledge across teams</li>
                  <li><strong>Innovation Culture</strong> - Encourage continuous learning and sharing</li>
                  <li><strong>Builder Culture</strong> - Inspire others through knowledge sharing and foster a culture of building and experimentation</li>
                </ul>
              </div>
            </div>
          </section>

          <section style={{ marginBottom: '32px' }}>
            <h2 style={{ color: '#2563eb', marginBottom: '16px', textAlign: 'center' }}>How It Works</h2>
            <ol style={{ paddingLeft: '170px', lineHeight: '1.8' }}>
              <li><strong>Create or join series</strong> - Set up a knowledge sharing series for your team</li>
              <li><strong>Submit topics</strong> - Share something you've learned or built</li>
              <li><strong>Attend sessions</strong> - Learn from colleagues' presentations</li>
              <li><strong>Prepare for conversations</strong> - Using generated prep notes</li>
              <li><strong>Provide feedback</strong> - Help track impact</li>
              <li><strong>Track progress</strong> - See how your contributions help the team</li>
            </ol>
          </section>

          <section style={{ marginBottom: '32px' }}>
            <h2 style={{ color: '#2563eb', marginBottom: '16px', textAlign: 'center' }}>Getting Started</h2>
            <p>
              Ready to transform how your team shares knowledge? Start by exploring existing series or creating 
              a new one for your team. Every expert was once a beginner - share what you know and learn from others!
            </p>
          </section>

          <section style={{ 
            marginBottom: '32px', 
            backgroundColor: '#fef3c7', 
            border: '1px solid #f59e0b', 
            borderRadius: '8px', 
            padding: '20px' 
          }}>
            <h2 style={{ color: '#92400e', marginBottom: '12px', textAlign: 'center', fontSize: '1.1rem' }}>⚠️ Data & Usage Notice</h2>
            <p style={{ textAlign: 'center', margin: '0', color: '#78350f', fontSize: '14px' }}>
              This application is for <strong>internal Amazon use only</strong>. ShareBrief is designed solely to manage 
              knowledge sharing sessions — it is not intended to store customer data, personal information, or any 
              sensitive or confidential content. Please limit entries to topic titles, descriptions, and presentation 
              metadata only.
            </p>
          </section>


          {/* Footer */}
          <div style={{ 
            textAlign: 'center', 
            marginTop: '60px', 
            paddingTop: '40px',
            borderTop: '1px solid #e0e0e0',
            maxWidth: '300px',
            margin: '60px auto 0 auto'
          }}>
            <div style={{ 
              fontSize: '14px', 
              color: '#666',
              lineHeight: '1.4'
            }}>
              Made in <svg width="26" height="18" viewBox="0 0 640 480" style={{ verticalAlign: 'middle', margin: '0 3px' }} xmlns="http://www.w3.org/2000/svg"><title>Canada</title><path fill="#fff" d="M150.1 0h339.7v480H150z"/><path fill="#d52b1e" d="M-19.7 0h169.8v480H-19.7zm509.5 0h169.8v480H489.9zM201 232l-13.3 4.4 61.4 54c4.7 13.7-1.6 17.8-5.6 25l66.6-8.4-1.6 67 13.9-.3-3.1-66.6 66.7 8c-4.1-8.7-7.8-13.3-4-27.2l61.3-51-10.7-4c-8.8-6.8 3.8-32.6 5.6-48.9 0 0-35.7 12.3-38 5.8l-9.2-17.5-32.6 35.8c-3.5.9-5-.5-5.9-3.5l15-74.8-23.8 13.4q-3.2 1.3-5.2-2.2l-23-46-23.6 47.8q-2.8 2.5-5 .7L264 130.8l13.7 74.1c-1.1 3-3.7 3.8-6.7 2.2l-31.2-35.3c-4 6.5-6.8 17.1-12.2 19.5s-23.5-4.5-35.6-7c4.2 14.8 17 39.6 9 47.7"/></svg> with &hearts; by <a href="https://phonetool.amazon.com/users/ryanmasc" target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb', textDecoration: 'none' }} onMouseEnter={(e) => e.target.style.textDecoration = 'underline'} onMouseLeave={(e) => e.target.style.textDecoration = 'none'}>ryanmasc@</a>
              <br/>
              December 2025
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default AboutPage;
